import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import utils from "@src/utils";
import { useRouter } from "next/router";
import { ErrorAlert, Input } from "@components/common";
import {
  OtpPurposeRegister,
  signUp,
  SignUpRequest,
} from "@src/utils/api/account";
import { FadeIn } from "@components/common/Animation/FadeIn";
import OtpSubForm from "@components/account/OtpSubForm";
import PasswordSubForm from "@components/account/PasswordSubForm";
import { HttpError } from "@utils/http";
import ShapeCaptDialog from "./ShapeCaptDialog";
import { sendSuccessNotification } from "@src/utils";
import { useAppDispatch } from "@src/store";

export default function SignupForm() {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const [isPasswordStrong, setIsPasswordStrong] = useState(false);
  const [repeatedPasswordMatch, setRepeatedPasswordMatch] = useState(false);
  // captcha
  const [captchaDialogOpen, setCaptchaDialogOpen] = useState(false);
  const [formData, setFormData] = useState<SignUpRequest>();

  const router = useRouter();

  const dispatch = useAppDispatch();

  const handleSignUp = async (captcha: SignUpRequest["captcha"]) => {
    try {
      if (!formData) return;
      formData.captcha = captcha;
      await signUp(formData as SignUpRequest, t);
      sendSuccessNotification(t("account.signUp.success"), dispatch);
      router.replace("/account/signin");
    } catch (e) {
      console.error(e);

      if (e instanceof HttpError) {
        setErrorMessage(e.message);
      } else {
        setErrorMessage(t("common.notification.unknownError") as string);
      }
    }
  };

  async function submitForm(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMessage("");
    // focus on the password bar if the password is not strong enough
    if (!isPasswordStrong) {
      setErrorMessage(t("account.common.pwdWeakError"));
      const passwordBar = document.getElementById(
        "password"
      ) as HTMLInputElement;
      passwordBar.focus();
      return;
    }

    if (!repeatedPasswordMatch) {
      const repeatPasswordBar = document.getElementById(
        "repeatPassword"
      ) as HTMLInputElement;
      repeatPasswordBar.focus();
      return;
    }

    const target = e.target as HTMLFormElement;
    const data = utils.formDataToObject(new FormData(target)) as SignUpRequest;
    setFormData(data);
    setCaptchaDialogOpen(true);
  }

  return (
    <form className="space-y-6" onSubmit={submitForm}>
      {/* Username */}
      <div>
        <label
          htmlFor="username"
          className="block text-sm font-medium text-gray-700"
        >
          {t("account.common.username")}
        </label>
        <div className="mt-1">
          <input
            id="username"
            name="username"
            type="text"
            autoComplete="username"
            required
            className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
          />
        </div>
      </div>

      {/* Password */}
      <PasswordSubForm
        setIsPasswordStrong={setIsPasswordStrong}
        setRepeatedPasswordMatch={setRepeatedPasswordMatch}
      />

      <OtpSubForm
        setErrorMessage={setErrorMessage}
        mobileFormId={"mobile"}
        otpFormId={"otp"}
        purpose={OtpPurposeRegister}
      />

      <div>
        <Input
          label={t("account.common.invitationCode")!}
          name="invitationCode"
          defaultValue={router.query["invitation_code"] || ""}
        />
      </div>

      {/* tickbox for privacy and service */}
      <div className="relative flex items-start">
        <div className="flex h-6 items-center">
          <input
            id="agreement"
            aria-describedby="candidates-description"
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
            required
          />
        </div>
        <div className="ml-3 text-sm leading-6">
          <label htmlFor="candidates" className="font-medium text-gray-900">
            {t("account.agreement.prefix")}{" "}
            <a href="/agreement/privacy" target="_blank" rel="noopener">
              <span className="underline">
                {t("account.agreement.policyPolicy")}
              </span>
            </a>
            {" & "}
            <a href="/agreement/service" target="_blank" rel="noopener">
              <span className="underline">
                {t("account.agreement.serviceAgreement")}
              </span>
            </a>
          </label>
        </div>
      </div>
      <ShapeCaptDialog
        open={captchaDialogOpen}
        setOpen={setCaptchaDialogOpen}
        onConfirm={handleSignUp}
      />

      {/* Error Alert */}
      {errorMessage && (
        <FadeIn>
          <ErrorAlert title={errorMessage} />
        </FadeIn>
      )}

      {/* Submit Button */}
      <div>
        <button
          type="submit"
          className="flex w-full justify-center rounded-md border border-transparent bg-primary py-2 px-4 text-sm font-medium shadow-sm hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500"
        >
          {t("account.actions.signUp")}
        </button>
      </div>
    </form>
  );
}
